import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateTableDto,
  UpdateTableDto,
  createTable,
  deleteTable,
  getTable,
  getTables,
  updateTable,
} from '../../api/tableApi';

const useTables = (tableId?: string, storeId?: string) => {
  const queryClient = useQueryClient();

  const tablesQuery = useQuery({
    queryKey: ['getTables', storeId],
    queryFn: () => getTables(storeId),
  });

  const tableQuery = useQuery({
    queryKey: ['getTable', tableId],
    queryFn: () => (tableId ? getTable(tableId) : null),
    enabled: !!tableId,
  });

  const addTableMutation = useMutation({
    mutationKey: ['addTable'],
    mutationFn: (data: CreateTableDto) => createTable(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getTables'] });
    },
  });

  const updateTableMutation = useMutation({
    mutationKey: ['updateTable'],
    mutationFn: ({ id, data }: { id: string; data: UpdateTableDto }) => updateTable(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getTables'] });
      queryClient.invalidateQueries({ queryKey: ['getTable'] });
    },
  });

  const deleteTableMutation = useMutation({
    mutationKey: ['deleteTable'],
    mutationFn: (tableId: string) => deleteTable(tableId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getTables'] });
    },
  });

  return {
    tables: tablesQuery.data,
    tablesLoading: tablesQuery.isPending,
    tablesError: tablesQuery.isError ? tablesQuery.error : null,

    table: tableQuery.data,
    tableLoading: tableQuery.isPending && tableQuery.isFetching,
    tableError: tableQuery.isError ? tableQuery.error : null,

    addTable: addTableMutation.mutateAsync,
    addTableLoading: addTableMutation.isPending,

    updateTable: updateTableMutation.mutateAsync,
    updateTableLoading: updateTableMutation.isPending,

    deleteTable: deleteTableMutation.mutateAsync,
    deleteTableLoading: deleteTableMutation.isPending,
  };
};

export default useTables;
